import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Stack,
  useTheme,
} from "@mui/material";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import backgroundImg from "./assets/pexels-zain-naqvi-246994819-16285228.jpg";
import profileImg from "./assets/PhotOle_0010_JPG.jpg";
import { DIGITAL_BUSINESS_CARDS } from "./cardData";

const AmerCard = () => {
  const theme = useTheme();
  const cardData = DIGITAL_BUSINESS_CARDS.amer;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contactItems = [
    {
      id: "phone",
      label: "Phone",
      value: cardData.phone,
      href: `tel:${cardData.phone}`,
      icon: (
        <CallRoundedIcon
          sx={{ color: theme.palette.primary.main, fontSize: 40 }}
        />
      ),
    },
    {
      id: "whatsapp",
      label: "Whatsapp",
      value: cardData.phone,
      href: cardData.whatsapp,
      icon: (
        <WhatsAppIcon
          sx={{ color: theme.palette.primary.main, fontSize: 40 }}
        />
      ),
    },
    {
      id: "email",
      label: "Email",
      value: cardData.email,
      href: `mailto:${cardData.email}`,
      icon: (
        <EmailRoundedIcon
          sx={{ color: theme.palette.primary.main, fontSize: 40 }}
        />
      ),
    },
    {
      id: "linkedin",
      label: "LinkedIn",
      value: cardData.name,
      href: cardData.linkedin,
      icon: (
        <LinkedInIcon
          sx={{ color: theme.palette.primary.main, fontSize: 40 }}
        />
      ),
    },
    {
      id: "address",
      label: "Address",
      value: cardData.address,
      icon: (
        <LocationOnRoundedIcon
          sx={{ color: theme.palette.primary.main, fontSize: 40 }}
        />
      ),
    },
  ];

  const handleSaveContact = () => {
    // Create vCard
    const vcard = `BEGIN:VCARD
VERSION:4.0
FN:${cardData.name}
TITLE:${cardData.title}
ORG:${cardData.company}
TEL;TYPE=work,voice:${cardData.phone}
EMAIL:${cardData.email}
ADR;TYPE=work:;;${cardData.address}
END:VCARD`;

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("download", `${cardData.name.replace(/\s+/g, "_")}.vcf`);
    link.href = url;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        paddingTop: isMobile ? "15vh" : "20vh",
        paddingBottom: isMobile ? "3vh" : "5vh",
        overflow: "hidden",
        //display: "flex",
        //flexDirection: "column",
        //alignItems: "center",
        //justifyContent: "center",
      }}
    >
      {/* Background Image - Full Screen */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: isMobile ? "100%" : "824px",
          height: "100%",
          zIndex: 0,
        }}
      >
        <Box
          component="img"
          src={backgroundImg}
          alt="Background"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
          }}
        />
      </Box>

      {/* Main Card - wraps content */}
      <Box
        sx={{
          position: "relative",
          left: "50%",
          transform: "translateX(-50%)",
          width: isMobile ? "100%" : "90%",
          maxWidth: 500,
          background: theme.palette.background.alt,
          borderRadius: isMobile ? "16px" : "24px",
          boxShadow:
            "0 8px 32px rgba(0, 0, 0, 0.3), 0 4px 16px rgba(0, 0, 0, 0.2)",
          display: "flex",
          flexDirection: "column",
          zIndex: 5,
          pb: 1,
        }}
      >
        {/* Profile Picture Ripple - using box-shadow */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? 140 : 180,
            height: isMobile ? 140 : 180,
            borderRadius: "50%",
            backgroundColor: "transparent",
            zIndex: 7,
            "@keyframes ripple": {
              "0%": {
                boxShadow: `0 0 0 0 ${theme.palette.primary.main}33,
                            0 0 0 0.4em ${theme.palette.primary.main}33,
                            0 0 0 1.2em ${theme.palette.primary.main}33,
                            0 0 0 2.0em ${theme.palette.primary.main}33`,
              },
              "100%": {
                boxShadow: `0 0 0 0.4em ${theme.palette.primary.main}33,
                            0 0 0 1.2em ${theme.palette.primary.main}33,
                            0 0 0 2.0em ${theme.palette.primary.main}33,
                            0 0 0 2.8em ${theme.palette.primary.main}00`,
              },
            },
            animation: "ripple 1.2s linear infinite",
          }}
        />

        {/* Profile Picture - centered on top edge */}
        <Box
          component="img"
          src={profileImg}
          alt="Amer Al Fayadh"
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? 140 : 180,
            height: isMobile ? 140 : 180,
            borderRadius: "50%",
            border: isMobile ? `3px solid ${theme.palette.background.alt}` : `4px solid ${theme.palette.background.alt}`,
            objectFit: "cover",
            zIndex: 10,
          }}
        />

        {/* Card Content */}
        <Box
          sx={{
            pt: isMobile ? 13 : 18,
            pb: isMobile ? 2 : 3,
            px: isMobile ? 1 : 3,
            textAlign: "center",
            borderRadius: isMobile ? "16px" : "24px",
            display: "flex",
            flexDirection: "column",
            maxHeight: isMobile ? "calc(100vh - 18vh)" : "calc(100vh - 25vh)",
            overflow: "hidden",
          }}
        >
          {/* Header - Non-scrollable */}
          <Box sx={{ mb: 2, flex: 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: theme.palette.text.primary,
                mb: 0.5,
                fontSize: isMobile ? "1.25rem" : "1.5rem",
              }}
            >
              {cardData.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: "bold",
                display: "block",
              }}
            >
              {cardData.company}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 900,
                fontSize: "inherit",
                color: theme.palette.text.primary,
                display: "block",
              }}
            >
              {cardData.title}
            </Typography>
          </Box>

          {/* Save Contact Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSaveContact}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: isMobile ? "12px" : "15px",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "15px",
                py: isMobile ? 1.2 : 1.5,
                "&:hover": {
                  backgroundColor: theme.palette.primary[600],
                },
              }}
            >
              Save Contact
            </Button>
          </Box>

          {/* Contact Items */}
          <Stack
            spacing={2}
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: theme.palette.primary.main,
                borderRadius: "3px",
              },
            }}
          >
            {contactItems.map((item) => (
              <Card
                key={item.id}
                component={item.href ? "a" : "div"}
                href={item.href || undefined}
                target={
                  item.href &&
                  (item.id === "linkedin" ||
                    item.id === "email" ||
                    item.id === "whatsapp")
                    ? "_blank"
                    : undefined
                }
                rel={
                  item.href &&
                  (item.id === "linkedin" ||
                    item.id === "email" ||
                    item.id === "whatsapp")
                    ? "noopener noreferrer"
                    : undefined
                }
                sx={{
                  height: isMobile ? 60 : 65,
                  minHeight: isMobile ? 60 : 65,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: isMobile ? "12px" : "15px",
                  display: "flex",
                  alignItems: "center",
                  p: isMobile ? 1.5 : 2,

                  boxShadow: 2,
                  textDecoration: "none",
                  cursor: item.href ? "pointer" : "default",
                  "&:hover": item.href
                    ? {
                        boxShadow: 4,
                        transform: "translateY(-2px)",
                        transition: "all 0.2s ease",
                      }
                    : {},
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    minWidth: isMobile ? 40 : 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {item.icon}
                </Box>

                {/* Text Content */}
                <Box sx={{ ml: isMobile ? 1.5 : 2, flex: 1, textAlign: "start" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                      fontSize: isMobile ? "16px" : "18px",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: { xs: "10px", sm: "14px" },
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default AmerCard;
